import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Search, Trash2, Copy, Check, Pencil, X, Save, Loader2, Sparkles, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["design", "copy", "brief", "research", "other"];
const CAT_COLORS = {
  design: "bg-[#F59073]/10 text-[#F59073] border-[#F59073]/30",
  copy: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  brief: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  research: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  other: "bg-[#1E3050]/40 text-[#857973] border-[#1E3050]",
};

export default function PromptsPage() {
  const [prompts, setPrompts] = useState([]);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", content: "", category: "design", tags: "" });
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/prompts`, { withCredentials: true });
    setPrompts(data);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ name: "", content: "", category: "design", tags: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        content: form.content,
        category: form.category,
        tags: typeof form.tags === "string" ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : form.tags,
      };
      if (editingId) {
        await axios.put(`${API}/admin/prompts/${editingId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/prompts`, payload, { withCredentials: true });
      }
      resetForm();
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save prompt");
    }
    setSaving(false);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      content: p.content || "",
      category: p.category || "design",
      tags: Array.isArray(p.tags) ? p.tags.join(", ") : (p.tags || ""),
    });
    setShowForm(true);
  };

  const removePrompt = async (id) => {
    if (!window.confirm("Delete this prompt?")) return;
    await axios.delete(`${API}/admin/prompts/${id}`, { withCredentials: true });
    fetchData();
  };

  const copyToClipboard = async (p) => {
    try {
      await navigator.clipboard.writeText(p.content || "");
      setCopiedId(p.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      alert("Could not copy. Select the text manually.");
    }
  };

  const filtered = prompts.filter(p => {
    if (filterCat !== "all" && p.category !== filterCat) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.name?.toLowerCase().includes(q) ||
      p.content?.toLowerCase().includes(q) ||
      (Array.isArray(p.tags) ? p.tags.join(" ") : "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="prompts-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#F59073]" /> Prompt Library
          </h1>
          <p className="text-sm text-[#857973]">{prompts.length} saved prompts · click to copy</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} data-testid="new-prompt-btn"
          className="flex items-center gap-2 bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all">
          <Plus className="w-4 h-4" /> New Prompt
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857973]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, content, tag..." data-testid="prompts-search"
            className="w-full bg-[#0F1F3A] border border-[#1E3050] text-white pl-10 pr-4 py-2.5 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
        </div>
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setFilterCat("all")} className={`px-3 py-2 rounded-sm text-xs font-mono transition-colors ${filterCat === "all" ? "bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/30" : "bg-[#0F1F3A] text-[#857973] border border-[#1E3050] hover:text-white"}`}>all</button>
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setFilterCat(c)} className={`px-3 py-2 rounded-sm text-xs font-mono transition-colors ${filterCat === c ? CAT_COLORS[c] : "bg-[#0F1F3A] text-[#857973] border border-[#1E3050] hover:text-white"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-5 mb-6" data-testid="prompt-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit text-white">{editingId ? "Edit prompt" : "New prompt"}</h3>
            <button onClick={resetForm} className="text-[#857973] hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Prompt name (e.g. 'Landing page hero — SaaS')" data-testid="prompt-name"
                className="md:col-span-2 bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))}
              placeholder="Tags (comma separated, e.g. saas, landing, dark)"
              className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
            <textarea required value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
              rows={14} placeholder="The full prompt. Use {placeholders} you'll replace manually before pasting..." data-testid="prompt-content"
              className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-3 rounded-sm text-sm focus:border-[#D9A872] outline-none resize-y font-mono leading-relaxed" />
            <div className="flex items-center gap-2 justify-end">
              <button type="button" onClick={resetForm} className="text-[#857973] hover:text-white text-sm px-4 py-2">Cancel</button>
              <button type="submit" disabled={saving || !form.name.trim() || !form.content.trim()} data-testid="save-prompt-btn"
                className="flex items-center gap-2 bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingId ? "Save changes" : "Save prompt"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {filtered.map(p => {
          const expanded = expandedId === p.id;
          const preview = (p.content || "").slice(0, 240);
          const truncated = (p.content || "").length > 240;
          return (
            <div key={p.id} data-testid={`prompt-card-${p.id}`}
              className="bg-[#0F1F3A] border border-[#1E3050] hover:border-[#F59073]/40 rounded-sm p-4 transition-colors group">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-outfit text-white text-base mb-1">{p.name}</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] px-2 py-0.5 rounded-sm font-mono border ${CAT_COLORS[p.category] || CAT_COLORS.other}`}>{p.category}</span>
                    {Array.isArray(p.tags) && p.tags.map((t, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-sm bg-[#1E3050]/40 text-[#857973] flex items-center gap-1">
                        <Tag className="w-2.5 h-2.5" /> {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => startEdit(p)} className="text-[#857973] hover:text-[#F59073] p-1.5" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => removePrompt(p.id)} className="text-[#857973] hover:text-red-400 p-1.5" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>

              <pre className="font-mono text-xs text-[#857973] whitespace-pre-wrap leading-relaxed mb-3 cursor-pointer" onClick={() => setExpandedId(expanded ? null : p.id)}>
                {expanded ? p.content : preview}{!expanded && truncated && "..."}
              </pre>
              {truncated && (
                <button onClick={() => setExpandedId(expanded ? null : p.id)} className="text-[10px] text-[#F59073] hover:underline mb-2">
                  {expanded ? "Show less" : "Show full prompt"}
                </button>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-[#1E3050]">
                <span className="text-[10px] font-mono text-[#857973]">
                  {p.created_at?.slice(0, 10)}
                </span>
                <button onClick={() => copyToClipboard(p)} data-testid={`copy-prompt-${p.id}`}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm font-medium transition-colors ${copiedId === p.id ? "bg-green-500/10 text-green-400 border border-green-500/30" : "bg-[#F59073] text-black hover:bg-[#F59073]/90"}`}>
                  {copiedId === p.id ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy prompt</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 border border-dashed border-[#1E3050] rounded-sm">
          <Sparkles className="w-10 h-10 text-[#1E3050] mx-auto mb-3" />
          <p className="text-[#857973] text-sm mb-1">{prompts.length === 0 ? "No prompts saved yet." : "No prompts match your filter."}</p>
          {prompts.length === 0 && <p className="text-[#857973] text-xs">Click "New Prompt" to save your first design prompt.</p>}
        </div>
      )}
    </div>
  );
}
